import { useState, useCallback } from 'react';
import Cookies from 'js-cookie';

const API_BASE_URL = process.env.REACT_APP_API_HOST;

type GetArgs = {
  endpoint: string;
  params?: Record<string, string>;
  skipAuth?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess?: (data: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (err: any) => void;
  onFinally?: () => void;
}

type Get = (args: GetArgs) => Promise<void>;

type PostArgs = {
  endpoint: string;
  body?: Record<string, string | number | undefined | null | string[] | boolean>;
  params?: URLSearchParams;
  formData?: FormData;
  method?: 'POST' | 'PUT' | 'PATCH'| 'DELETE';
  skipAuth?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess?: (data: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (err: any) => void;
  onFinally?: () => void;
}

type Post = (args: PostArgs) => Promise<void>;

type UseApi = {
  (): { get: Get, post: Post, loading: boolean };
}

export const useApi: UseApi = () => {
  const [ loading, setLoading] = useState(false);

  const execFetch = async (args: GetArgs | PostArgs, req: Request) => {
    try {
      setLoading(true);
      const res = await fetch(req);

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const err = new Error(
          `[${ req.method } failed] path: ${ res.url } ${res.status} ${res.statusText}`
        ) as Error & { data: unknown };
        err.data = errorData;
        throw err;
      }

      const data = await res.json();

      console.log(`${ req.method } succeeded!!`);
      args?.onSuccess?.(data);
    } catch (e) {
      args?.onError?.(e);
      throw new Error(`[${ req.method } error caught] ${ e }`);
    } finally {
      args?.onFinally?.();
      setLoading(false)
    }
  }

  const get = useCallback<Get>(
    async (args: GetArgs) => {
      const accessToken = Cookies.get('access_token');
      if (!args.skipAuth && !accessToken) {
        const err = new Error('No credentials!');
        args?.onError?.(err);
        throw err;
      }

      const url = new URL(`${ API_BASE_URL }/${ args.endpoint }?${ new URLSearchParams(args.params) }`);

      const req = new Request(url.href, {
        method: 'GET',
        headers: new Headers({
          'Authorization': `Bearer ${ accessToken }`
        }),
      });

      execFetch(args, req);
    }, []
  );

  const post = useCallback<Post>(
    async (args: PostArgs) => {
      const accessToken = Cookies.get('access_token');
      if (!args.skipAuth && !accessToken) {
        const err = new Error('No credentials!');
        args?.onError?.(err);
        throw err;
      }

      const url = new URL(`${ API_BASE_URL }/${ args.endpoint }`);

      const headers = new Headers({
        'Authorization': `Bearer ${ accessToken }`
      });

      if (args.body) {
        headers.append('Content-Type', 'application/json');
      }

      const req = new Request(url.href, {
        method: args.method || 'POST',
        headers,
        body: args.body ? JSON.stringify(args.body) : args.params || args.formData,
      });

      execFetch(args, req);
    }, []
  );

  return { get, post, loading };
}
